import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Chip, cn } from '@c2fo/liquidity';
import { CheckCircleIcon, CheckIcon } from '@c2fo/liquidity/icons';
import useInstantPayEligible from '@/enterprise/components/InstantPay/useInstantPayEligible';
import { CombinedPricingSuggestion } from '@/enterprise/data/usePricingSuggestion';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { useReporting } from '@/reporting';
import IntercomDataTarget from '@/reporting/IntercomDataTarget';
import { RateType, useOfferBuilderContext } from '../../OfferBuilderContext';
import {
  buildTakerMarketKey,
  getExistingRateFromConfig,
  hasExpressAcceptForMarket,
  hasTrendingRateForMarket,
  buildBulkRatePayload,
} from '../../utils';
import BulkNameYourRateModal from './BulkNameYourRateModal';
import BulkStrategyModal from './BulkStrategyModal';

type PricingStrategy = 'expressAccept' | 'trendingRate' | 'existingRate' | 'nameYourRate';
type StrategyModalType = 'expressAccept' | 'trendingRate' | 'existingRate' | null;

interface ApplyToAllOptionsProps {
  takerMarkets: TakerMarket[];
  pricingData: CombinedPricingSuggestion | undefined;
  selectedRateType: RateType;
}

const ApplyToAllOptions = ({ takerMarkets, pricingData, selectedRateType }: ApplyToAllOptionsProps) => {
  const { t } = useTranslation();
  const { dispatch, state } = useOfferBuilderContext();
  const { frequency, workingTakerMarkets, selected } = state;
  const { trackEnterpriseEvent } = useReporting();
  const { isInstantPayEligible } = useInstantPayEligible();
  const [nameYourRateModalOpen, setNameYourRateModalOpen] = useState(false);
  const [strategyModalOpen, setStrategyModalOpen] = useState<StrategyModalType>(null);

  // Determine current baseline strategy by checking non-overridden markets
  const currentStrategy = useMemo((): PricingStrategy | null => {
    if (takerMarkets.length === 0) {
      return null;
    }

    // Get non-overridden markets with eligible amounts (these represent the baseline)
    const baselineMarkets = takerMarkets.filter((tm) => {
      if (tm.eligibleInvoiceAmount <= 0) return false;
      const marketKey = buildTakerMarketKey(tm);
      const market = workingTakerMarkets[marketKey];
      return !market?.isIndividuallyOverridden;
    });

    if (baselineMarkets.length === 0) {
      return null;
    }

    // Check if all baseline markets have rates
    const baselineHaveRates = baselineMarkets.every((tm) => {
      const marketKey = buildTakerMarketKey(tm);
      const market = workingTakerMarkets[marketKey];
      return market?.rate !== undefined && market?.rate !== null && market?.pricingStrategy;
    });

    if (!baselineHaveRates) {
      return null;
    }

    // Get the pricing strategy from the first baseline market
    // All baseline markets should have the same strategy since they were set by bulk action
    const firstMarketKey = buildTakerMarketKey(baselineMarkets[0]);
    const firstMarket = workingTakerMarkets[firstMarketKey];
    const baselineStrategy = firstMarket?.pricingStrategy ?? null;

    if (!baselineStrategy) {
      return null;
    }

    // Verify all baseline markets have the same strategy
    const allSameStrategy = baselineMarkets.every((tm) => {
      const marketKey = buildTakerMarketKey(tm);
      const market = workingTakerMarkets[marketKey];
      return market?.pricingStrategy === baselineStrategy;
    });

    // Special case: if all baseline markets have nameYourRate strategy and matching config rates,
    // consider it as 'existingRate' strategy (rates imported from config)
    if (allSameStrategy && baselineStrategy === 'nameYourRate') {
      const allMatchConfigRates = baselineMarkets.every((tm) => {
        const marketKey = buildTakerMarketKey(tm);
        const market = workingTakerMarkets[marketKey];
        const configRate = getExistingRateFromConfig(tm, market?.rateType ?? selectedRateType);

        return configRate !== null && market?.rate === configRate.rate && market?.rateType === configRate.rateType;
      });

      if (allMatchConfigRates) {
        return 'existingRate';
      }
    }

    return allSameStrategy ? baselineStrategy : null;
  }, [takerMarkets, workingTakerMarkets, selectedRateType]);

  // Check if Trending Rate is available for ANY markets with eligible amount
  const hasTrendingRate = useMemo(() => {
    return takerMarkets.some((tm) => hasTrendingRateForMarket(tm, pricingData, selectedRateType, frequency));
  }, [frequency, takerMarkets, pricingData, selectedRateType]);

  // Check if Express Accept is available for ANY markets
  const hasExpressAccept = useMemo(() => {
    if (frequency !== 'oneTime') {
      return false;
    }

    return takerMarkets.some((tm) =>
      hasExpressAcceptForMarket(tm, pricingData, selectedRateType, isInstantPayEligible)
    );
  }, [frequency, takerMarkets, pricingData, selectedRateType, isInstantPayEligible]);

  // Check if Existing Rate is available for ANY markets
  // Existing rates from config can exist regardless of current eligible amount
  const hasExistingRate = useMemo(() => {
    return takerMarkets.some((tm) => {
      const existingRate = getExistingRateFromConfig(tm, selectedRateType);
      return existingRate !== null;
    });
  }, [takerMarkets, selectedRateType]);

  // Calculate rate selection counts including all markets (name your rate + fixed rate)
  const { selectedCount, totalCount } = useMemo(() => {
    // Total count: all selected markets (not just name your rate markets)
    const total = selected.length;

    // Selected count: markets that have a rate selected
    const selectedWithRate = selected.filter((tm) => {
      const marketKey = buildTakerMarketKey(tm);
      const market = workingTakerMarkets[marketKey];

      // For price discovery markets, check if a rate is selected
      if (tm.marketPricingType === 'PRICE_DISCOVERY') {
        return market?.rate !== undefined && market?.rate !== null;
      }

      // For fixed rate markets (BENCHMARK or STATIC), they always have a rate
      return tm.marketPricingType === 'BENCHMARK' || tm.marketPricingType === 'STATIC';
    }).length;

    return { selectedCount: selectedWithRate, totalCount: total };
  }, [selected, workingTakerMarkets]);

  const handleStrategyClick = (strategy: 'expressAccept' | 'trendingRate' | 'existingRate') => {
    // Check if ALL divisions have the strategy available
    // Important: Check ALL divisions to determine if we need the modal
    // Don't skip divisions - if any division doesn't have the strategy, we need user input
    const allHaveStrategy = takerMarkets.every((tm) => {
      if (strategy === 'expressAccept') {
        return hasExpressAcceptForMarket(tm, pricingData, selectedRateType, isInstantPayEligible);
      } else if (strategy === 'trendingRate') {
        return hasTrendingRateForMarket(tm, pricingData, selectedRateType, frequency);
      } else if (strategy === 'existingRate') {
        return getExistingRateFromConfig(tm, selectedRateType) !== null;
      }

      return false;
    });

    // If all divisions have the strategy, apply directly without modal
    if (allHaveStrategy) {
      const payload = buildBulkRatePayload(
        takerMarkets,
        strategy,
        selectedRateType,
        frequency,
        pricingData,
        workingTakerMarkets,
        undefined,
        true, // skipMarketsWithZeroEligibleAmount
        isInstantPayEligible
      );

      if (payload.length > 0) {
        dispatch({
          type: 'UPDATE_WORKING_TAKER_MARKETS_RATE',
          payload,
        });

        dispatch({
          type: 'SET_RATE_TYPE',
          payload: selectedRateType,
        });

        trackEnterpriseEvent('offer-builder::apply-to-all::strategy-selected', {
          strategy,
          rateType: selectedRateType,
          totalMarkets: takerMarkets.length,
          marketsUpdated: payload.length,
          frequency,
        });
      }
    } else {
      // Show modal for user to input rate for divisions without strategy
      setStrategyModalOpen(strategy);
      trackEnterpriseEvent('offer-builder::apply-to-all::name-your-rate-opened', {
        strategy,
        totalMarkets: takerMarkets.length,
        frequency,
      });
    }
  };

  const handleNameYourRateClick = () => {
    setNameYourRateModalOpen(true);
    trackEnterpriseEvent('offer-builder::apply-to-all::name-your-rate-opened', {
      totalMarkets: takerMarkets.length,
      frequency,
    });
  };

  // Only show if there are multiple markets
  // Name Your Rate is always available for multiple markets
  if (takerMarkets.length <= 1) {
    return null;
  }

  return (
    <>
      <BulkStrategyModal
        takerMarkets={takerMarkets}
        strategy={strategyModalOpen ?? 'expressAccept'}
        pricingData={pricingData}
        open={strategyModalOpen !== null}
        onClose={() => setStrategyModalOpen(null)}
      />
      <BulkNameYourRateModal
        takerMarkets={takerMarkets}
        open={nameYourRateModalOpen}
        onClose={() => {
          setNameYourRateModalOpen(false);
          trackEnterpriseEvent('offer-builder::bulk-name-your-rate-modal::closed', {
            totalMarkets: takerMarkets.length,
          });
        }}
      />
      <IntercomDataTarget className="mb-2 rounded-xl bg-gray-100 px-4 py-2" target="offerBuilderSelectAllRates">
        <div className="flex justify-between gap-4 text-sm">
          <div>{t('builder.applyToAll')}</div>
          <div>
            <Chip
              className={cn('border-deepGray-500 bg-deepGray-50 font-medium text-deepGray-900', {
                'border-lightBlue-500 bg-lightBlue-500 text-text-primary': selectedCount === totalCount,
              })}
              isRound
              label={
                <span className="flex items-center gap-1.5">
                  {selectedCount === totalCount && <CheckCircleIcon className="size-3.5 fill-lightBlue-900" />}
                  {t('builder.ratesSelected', { selected: selectedCount, total: totalCount, count: totalCount })}
                </span>
              }
              size="xs"
              type="default"
              variant="outlined"
            />
          </div>
        </div>
        <div>
          <div className="mt-2 flex flex-col gap-1 md:flex-row md:flex-wrap">
            {hasExpressAccept && (
              <Button
                onClick={() => handleStrategyClick('expressAccept')}
                size="sm"
                variant={currentStrategy === 'expressAccept' ? 'primary' : 'tertiary'}
              >
                <span className="flex shrink-0 items-center gap-1">
                  {currentStrategy === 'expressAccept' && <CheckIcon className="size-5 fill-white" />}
                  {t('core.expressAccept')}
                </span>
              </Button>
            )}
            {hasTrendingRate && (
              <Button
                onClick={() => handleStrategyClick('trendingRate')}
                size="sm"
                variant={currentStrategy === 'trendingRate' ? 'primary' : 'tertiary'}
              >
                <span className="flex shrink-0 items-center gap-1">
                  {currentStrategy === 'trendingRate' && <CheckIcon className="size-5 fill-white" />}
                  {t('core.trending')}
                </span>
              </Button>
            )}
            {hasExistingRate && (
              <Button
                onClick={() => handleStrategyClick('existingRate')}
                size="sm"
                variant={currentStrategy === 'existingRate' ? 'primary' : 'tertiary'}
              >
                <span className="flex shrink-0 items-center gap-1">
                  {currentStrategy === 'existingRate' && <CheckIcon className="size-5 fill-white" />}
                  {t('builder.existingRate')}
                </span>
              </Button>
            )}
            <Button
              onClick={handleNameYourRateClick}
              size="sm"
              variant={currentStrategy === 'nameYourRate' ? 'primary' : 'tertiary'}
            >
              <span className="flex shrink-0 items-center gap-1">
                {currentStrategy === 'nameYourRate' && <CheckIcon className="size-5 fill-white" />}
                {t('core.nameYourRate')}
              </span>
            </Button>
          </div>
        </div>
      </IntercomDataTarget>
    </>
  );
};

export default ApplyToAllOptions;
